import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  MultiSelectOption,
  Text,
  TextInputV2,
  UniversalSelect,
  useToastMessage,
} from '@equip.health/ui';
import { useEffect, useMemo, useState } from 'react';
import { CarePlanInterventionResponse } from '~/lib/types/carePlan/carePlans';
import {
  useGetInterventionCategoriesAssigneesMapQuery,
  useGetInterventionCategoriesEnumQuery,
} from '~/store/services/refCarePlanEnums';
import {
  useGetCarePlansByTreatmentEpisodeIdQuery,
  useUpdateInterventionMutation,
} from '~/store/services/carePlan';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import Loading from '~/pages/loading';

const TOP_LEVEL_ID = 'edit-custom-intervention-modal';

type EditFormData = {
  description: string;
  selectedGoalIds: string[];
  selectedProviderTypes: string[];
};

const buildInitialFormData = (
  intervention: CarePlanInterventionResponse,
): EditFormData => ({
  description: intervention.description ?? '',
  selectedGoalIds: intervention.goalIds ?? [],
  selectedProviderTypes: [
    ...intervention.roles,
    ...(intervention.roleGroups ?? []),
  ],
});

const areFormsEqual = (a: EditFormData, b: EditFormData): boolean => {
  if (a.description !== b.description) return false;
  if (a.selectedGoalIds.length !== b.selectedGoalIds.length) return false;
  if (a.selectedProviderTypes.length !== b.selectedProviderTypes.length) {
    return false;
  }

  const byLocale = (x: string, y: string) => x.localeCompare(y);

  const sortedA = [...a.selectedGoalIds].sort(byLocale);
  const sortedB = [...b.selectedGoalIds].sort(byLocale);
  if (sortedA.some((id, i) => id !== sortedB[i])) return false;

  const sortedProvA = [...a.selectedProviderTypes].sort(byLocale);
  const sortedProvB = [...b.selectedProviderTypes].sort(byLocale);
  return sortedProvA.every((code, i) => code === sortedProvB[i]);
};

type Props = {
  onClose: () => void;
  onVoidClick: () => void;
  intervention: CarePlanInterventionResponse;
  treatmentEpisodeId: string;
};

export const EditCustomInterventionModal = ({
  onClose,
  onVoidClick,
  intervention,
  treatmentEpisodeId,
}: Props) => {
  const { createToast } = useToastMessage();

  const initialFormData = useMemo(
    () => buildInitialFormData(intervention),
    [intervention],
  );

  const [formData, setFormData] = useState<EditFormData>(initialFormData);

  const {
    data: categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetInterventionCategoriesEnumQuery();

  const {
    data: assigneesMap,
    isLoading: isAssigneesMapLoading,
    isError: isAssigneesMapError,
  } = useGetInterventionCategoriesAssigneesMapQuery();

  const {
    data: carePlans,
    isLoading: isCarePlansLoading,
    isError: isCarePlansError,
  } = useGetCarePlansByTreatmentEpisodeIdQuery(treatmentEpisodeId);

  const activeCarePlan = carePlans?.find(
    (cp) => cp.status.code === CarePlanStatus.ACTIVE,
  );

  const [
    updateIntervention,
    { isLoading: isUpdating, isError: isUpdateError },
  ] = useUpdateInterventionMutation();

  useEffect(() => {
    if (isUpdateError) {
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: `${TOP_LEVEL_ID}_error-toast`,
        text: 'We are unable to update the intervention. Please try again later.',
        variant: 'critical',
      });
    }
  }, [isUpdateError, createToast]);

  const goalOptions: MultiSelectOption[] = useMemo(
    () =>
      activeCarePlan?.problems.flatMap((problem) =>
        problem.goals.map((goal) => ({
          label: `${goal.pathwayGoal.label} (${problem.pathwayProblem.label})`,
          value: goal.id,
        })),
      ) ?? [],
    [activeCarePlan],
  );

  const categoryLabel = useMemo(() => {
    if (!categories || !intervention.categoryCode) return null;
    return (
      categories.find((cat) => cat.code === intervention.categoryCode)?.label ??
      null
    );
  }, [categories, intervention.categoryCode]);

  const providerOptions: MultiSelectOption[] = useMemo(() => {
    if (!assigneesMap || !intervention.categoryCode) return [];
    const assignees = assigneesMap[intervention.categoryCode] ?? [];
    return [...assignees]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((assignee) => ({
        label: assignee.label,
        value: assignee.code,
      }));
  }, [assigneesMap, intervention.categoryCode]);

  const isSaveEnabled =
    !areFormsEqual(formData, initialFormData) &&
    formData.description.trim().length > 0 &&
    formData.selectedGoalIds.length > 0 &&
    formData.selectedProviderTypes.length > 0;

  const handleSave = async () => {
    const assignees = assigneesMap?.[intervention.categoryCode ?? ''] ?? [];
    const providerTypes: string[] = [];
    const providerTypeGroups: string[] = [];

    formData.selectedProviderTypes.forEach((code) => {
      const assignee = assignees.find((a) => a.code === code);
      if (assignee?.type === 'INDIVIDUAL') {
        providerTypes.push(code);
      } else if (assignee?.type === 'GROUP') {
        providerTypeGroups.push(code);
      }
    });

    try {
      await updateIntervention({
        treatmentEpisodeId,
        interventionId: intervention.id,
        body: {
          description: formData.description,
          goalIds: formData.selectedGoalIds,
          providerTypes,
          providerTypeGroups,
        },
      }).unwrap();
      createToast({
        icon: FAIconName.checkRegular,
        id: `${TOP_LEVEL_ID}_success-toast`,
        text: 'Intervention updated.',
        variant: 'success',
      });
      onClose();
    } catch {
      // Error toast handled by isUpdateError useEffect.
    }
  };

  if (isCategoriesLoading || isAssigneesMapLoading || isCarePlansLoading) {
    return (
      <ModalV2
        bodyOverflow="visible"
        isOpen
        onClose={onClose}
        size="4xl"
        title="Edit intervention"
      >
        <Loading />
      </ModalV2>
    );
  }

  if (isCategoriesError || isAssigneesMapError || isCarePlansError) {
    return (
      <ModalV2
        bodyOverflow="visible"
        footerContent={
          <ChakraHStack justifyContent="space-between" width="100%">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__close`}
              leftIcon={FAIconName.timesRegular}
              onClick={onClose}
              variant="secondary"
            >
              Close
            </FireflyButton>
          </ChakraHStack>
        }
        isOpen
        onClose={onClose}
        size="4xl"
        title="Edit intervention"
      >
        <Text color="critical.base">
          Unable to load intervention data. Please try again later.
        </Text>
      </ModalV2>
    );
  }

  return (
    <ModalV2
      footerContent={
        <ChakraHStack
          justifyContent="space-between"
          paddingTop="14px"
          width="100%"
        >
          <FireflyButton
            id={`${TOP_LEVEL_ID}__void`}
            leftIcon={FAIconName.trashRegular}
            onClick={onVoidClick}
            variant="secondary"
          >
            Void
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__save`}
            isDisabled={!isSaveEnabled}
            isLoading={isUpdating}
            leftIcon={FAIconName.checkRegular}
            onClick={handleSave}
          >
            Save changes
          </FireflyButton>
        </ChakraHStack>
      }
      id={TOP_LEVEL_ID}
      isOpen
      onClose={onClose}
      size="4xl"
      title="Edit intervention"
    >
      <ChakraBox
        border="1px"
        borderColor="neutral.line"
        borderRadius="12px"
        marginTop="24px"
        padding="18px"
      >
        <ChakraFlex alignItems="center" gap="12px" marginBottom="18px">
          <ChakraBox flex="1">
            <Text marginBottom="6px" variant="bodyMedium">
              Category
            </Text>
            <Text>{categoryLabel ?? '—'}</Text>
          </ChakraBox>
          <Text>for</Text>
          <ChakraBox flex="1">
            <UniversalSelect
              ariaLabel="Goals"
              id={`${TOP_LEVEL_ID}__goals-select`}
              isSingleSelect={false}
              modalId={TOP_LEVEL_ID}
              onChange={(options: MultiSelectOption[]) => {
                setFormData((prev) => ({
                  ...prev,
                  selectedGoalIds: options.map((opt) => opt.value),
                }));
              }}
              options={goalOptions}
              placeholder="Select problems"
              value={goalOptions.filter((opt) =>
                formData.selectedGoalIds.includes(opt.value),
              )}
              withCheckBox
            />
          </ChakraBox>
        </ChakraFlex>

        <ChakraBox marginBottom="18px">
          <InputGroupV2 id={`${TOP_LEVEL_ID}__description-input-group`}>
            <TextInputV2
              id={`${TOP_LEVEL_ID}__description-input`}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, description: value }))
              }
              placeholder="Explain the recommended intervention"
              value={formData.description}
            />
          </InputGroupV2>
        </ChakraBox>

        <ChakraBox>
          <Text marginBottom="9px" variant="bodyMedium">
            Assigned to
          </Text>
          <UniversalSelect
            ariaLabel="Assigned to"
            id={`${TOP_LEVEL_ID}__providers-select`}
            isSingleSelect={false}
            modalId={TOP_LEVEL_ID}
            onChange={(options: MultiSelectOption[]) => {
              setFormData((prev) => ({
                ...prev,
                selectedProviderTypes: options.map((opt) => opt.value),
              }));
            }}
            options={providerOptions}
            placeholder="Select role"
            value={providerOptions.filter((opt) =>
              formData.selectedProviderTypes.includes(opt.value),
            )}
            withCheckBox
          />
        </ChakraBox>
      </ChakraBox>
    </ModalV2>
  );
};
